import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Download, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const categories = ['All', 'Portraits', 'Events', 'Graduation', 'Studio', 'Weddings'];

// Level Photography — Real Portfolio Assets
const LP_HERO = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/dj079our_8T8A9116.jpg";
const LP_FAMILY = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/h13bmgct_8T8A9862.jpg";
const LP_STUDIO = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/bf7hntnt_8T8A9929%202.jpg";
const LP_SENIOR = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/kxgqmo3l_8T8A9140%202.jpg";
const LP_GRAD = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/aupr3pky_8T8A9135.jpg";
const LP_WED_CAKE = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/8lzpy1z9_8T8A2923.jpg";
const LP_WED_FAMILY = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/xkf27m3o_8T8A2459.jpg";
const LP_WED_RECEPTION = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/kqcx6sq3_8T8A2926.jpg";
const LP_ANNIV_TRIO = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/apybx3mm_8T8A2511.jpg";
const LP_ANNIV_COUPLE = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/sefxu1vj_8T8A2479.jpg";

const defaultGalleryImages = [
  // Graduation
  { id: 1, image_url: LP_HERO, category: "Graduation", title: "Class of 2026 — Landy Joseph" },
  { id: 2, image_url: LP_GRAD, category: "Graduation", title: "Senior Cap & Gown" },
  { id: 3, image_url: LP_SENIOR, category: "Graduation", title: "Senior 2026" },
  // Weddings
  { id: 4, image_url: LP_WED_CAKE, category: "Weddings", title: "Cake Cutting" },
  { id: 5, image_url: LP_WED_FAMILY, category: "Weddings", title: "Bridal Family Portrait" },
  { id: 6, image_url: LP_WED_RECEPTION, category: "Weddings", title: "Reception Celebration" },
  // Birthdays / Events
  { id: 7, image_url: LP_ANNIV_TRIO, category: "Events", title: "Birthday Celebration — Babie" },
  { id: 8, image_url: LP_ANNIV_COUPLE, category: "Events", title: "Anniversary Suite" },
  // Studio / Portraits
  { id: 9, image_url: LP_STUDIO, category: "Studio", title: "Studio Portrait" },
  { id: 10, image_url: LP_FAMILY, category: "Portraits", title: "Mother & Daughter" },
  { id: 11, image_url: LP_STUDIO, category: "Portraits", title: "Editorial Portrait" },
  { id: 12, image_url: LP_FAMILY, category: "Studio", title: "Family Studio Session" },
];

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const { data } = await axios.get(`${API}/api/photos/public`);
        if (data.length > 0) {
          setPhotos(data.map(p => ({
            ...p,
            image_url: `${API}${p.image_url}`
          })));
        } else {
          setPhotos(defaultGalleryImages);
        }
      } catch (e) {
        console.error('Error fetching photos:', e);
        setPhotos(defaultGalleryImages);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  const filteredPhotos = selectedCategory === 'All' 
    ? photos 
    : photos.filter(p => p.category?.toLowerCase() === selectedCategory.toLowerCase());

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="gallery-page">
      {/* Header */}
      <section className="py-16 text-center">
        <div className="container-luxury">
          <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">Portfolio</p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6">
            Our Work
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Browse through our collection of professional photography spanning weddings, 
            portraits, events, and more.
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="pb-12">
        <div className="container-luxury">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`${
                  selectedCategory === category
                    ? 'btn-gold'
                    : 'bg-transparent text-gray-400 border border-[#333] hover:border-[#C9A14A] hover:text-[#C9A14A]'
                } px-6 py-2 text-xs uppercase tracking-wider transition-all duration-300`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-24">
        <div className="container-luxury">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="masonry-grid">
              {filteredPhotos.map((photo, index) => (
                <div
                  key={photo.id || index}
                  className="masonry-item img-zoom cursor-pointer group relative"
                  onClick={() => setSelectedImage(photo)}
                  data-testid={`gallery-item-${index}`}
                >
                  <img
                    src={photo.image_url}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-auto rounded-none"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="text-white text-sm uppercase tracking-wider">View</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredPhotos.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500">No photos found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl bg-black/95 border-[#333] p-0 overflow-hidden" data-testid="gallery-lightbox">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/80 rounded-full transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          {selectedImage && (
            <img
              src={selectedImage.image_url}
              alt="Gallery preview"
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
